import { ThemedFunctor } from '@doveit/bricks';
import { css } from 'styled-components';

export const base: ThemedFunctor = ({ theme }) => css`
  display: flex;
  gap: ${theme.remSize[200]};
  align-items: stretch;
  min-height: 560px;
`;

export const sideContainer = () => css`
  flex: 0 0 260px;
  position: relative;
`;

export const side: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.remSize[150]};
`;

export const filters = () => css`
  flex-grow: 0;
  flex-shrink: 0;
`;

export const legend: ThemedFunctor = ({ theme }) => css`
  display: flex;
  gap: ${theme.remSize[150]};
  align-items: center;
`;

export const legendItem: ThemedFunctor = ({ theme }) => css`
  display: flex;
  gap: ${theme.remSize[50]};
  align-items: center;
`;

export const legendCurrent: ThemedFunctor = ({ theme }) => css`
  width: ${theme.remSize[150]};
  height: ${theme.remSize[150]};
  border: 1px dashed ${theme.color.border.neutral.default.high};
`;

export const legendInGallery: ThemedFunctor = ({ theme }) => css`
  width: ${theme.remSize[150]};
  height: ${theme.remSize[150]};
  background-color: ${theme.color.background.neutral.default.high};
  opacity: .5;
`;

export const filmStripWrapper = () => css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const filmStrip = () => css`
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const filmStripFade = () => css`
  position: absolute;
  left: 0;
  right: 0;
  height: 40px;
  pointer-events: none;
  z-index: 4;
  transition: opacity 0.3s ease;
`;

export const filmStripFadeTop: ThemedFunctor = ({ theme }) => css`
  ${filmStripFade()}
  top: 0;
  background: linear-gradient(to bottom, ${theme.color.background.neutral.default.lowest}, transparent);
`;

export const filmStripFadeBottom: ThemedFunctor = ({ theme }) => css`
  ${filmStripFade()}
  bottom: 0;
  background: linear-gradient(to top, ${theme.color.background.neutral.default.lowest}, transparent);
`;

export const thumbnail: ThemedFunctor = ({ theme, $isCurrent }) => css`
  position: relative;

  ${$isCurrent && `
    outline: 2px dashed ${theme.color.border.primary.default.high};
    outline-offset: -2px;
  `}
`;

export const comparison: ThemedFunctor = ({ theme }) => css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.remSize[300]};
  padding: ${theme.remSize[300]};
  background-color: ${theme.color.background.neutral.default.low};
`;

export const currentPhoto: ThemedFunctor = () => css`
  flex: 0 0 220px;
  opacity: .8;
`;

export const newPhoto = () => css`
  flex: 1;
  min-width: 0;
`;

export const imageWrapper: ThemedFunctor = ({ theme, $highlighted }) => css`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  background-color: ${theme.color.background.neutral.default.low};

  ${$highlighted && `
    outline: 2px solid ${theme.color.border.primary.default.high};
  `}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const fileName: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  left: ${theme.remSize[100]};
  right: ${theme.remSize[100]};
  bottom: ${theme.remSize[100]};
  display: flex;
  justify-content: flex-end;
  min-width: 0;

  /* Badge does not truncate its label: constrain it and ellipsize the inner text */
  & > * {
    max-width: 100%;
    min-width: 0;
  }

  & > * > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const arrow: ThemedFunctor = ({ theme }) => css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.remSize[500]};
  height: ${theme.remSize[500]};
  border-radius: 50%;
  background-color: ${theme.color.background.neutral.default.lowest};
  color: ${theme.color.foreground.primary.default.high};
  box-shadow: 0 1px 4px rgba(0, 0, 0, .1);
`;

export const footer: ThemedFunctor = ({ theme, $isMobile }) => css`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.remSize[300]};

  ${$isMobile && `
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.remSize[150]};
  `}
`;

export const footerActions: ThemedFunctor = ({ theme, $isMobile }) => css`
  display: flex;
  gap: ${theme.remSize[150]};

  ${$isMobile && `
    & > * {
      flex: 1;
    }
  `}
`;

export const mobileBase: ThemedFunctor = ({ theme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.remSize[200]};
`;

export const mobileComparison: ThemedFunctor = ({ theme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.remSize[100]};
  padding: ${theme.remSize[150]};
  background-color: ${theme.color.background.neutral.default.low};
  scroll-margin-top: ${theme.remSize[200]};
`;

export const mobileCurrentPhoto: ThemedFunctor = ({ theme }) => css`
  display: flex;
  align-items: center;
  gap: ${theme.remSize[150]};
  opacity: .8;
`;

export const mobileCurrentPhotoImage = () => css`
  flex: 0 0 96px;
`;

export const mobileArrow = () => css`
  display: flex;
  justify-content: center;
`;

export const aboutAutocomplete: ThemedFunctor = ({ $isMobile }) => css`
  width: ${$isMobile ? '100%' : '250px'};
`;
